import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AspectRatioCalculator = () => {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleWidthChange = (e) => {
    const value = e.target.value;
    setWidth(value);
    setError('');
  };

  const handleHeightChange = (e) => {
    const value = e.target.value;
    setHeight(value);
    setError('');
  };

  const calculateDimensions = () => {
    if (!width && !height) {
      setError('Please enter either width or height');
      return;
    }

    if (width && isNaN(width)) {
      setError('Please enter a valid number for width');
      return;
    }

    if (height && isNaN(height)) {
      setError('Please enter a valid number for height');
      return;
    }

    let calculatedWidth, calculatedHeight;

    if (width) {
      calculatedHeight = (parseFloat(width) * 10) / 16;
      setHeight(calculatedHeight.toFixed(2));
    } else if (height) {
      calculatedWidth = (parseFloat(height) * 16) / 10;
      setWidth(calculatedWidth.toFixed(2));
    }

    // Create visualization data
    const labels = ['Width', 'Height'];
    const dimensions = [calculatedWidth || width, calculatedHeight || height];

    const chartData = {
      labels,
      datasets: [
        {
          label: 'Dimensions',
          data: dimensions,
          backgroundColor: ['rgba(255, 99, 132, 0.5)', 'rgba(54, 162, 235, 0.5)'],
          borderColor: ['rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)'],
          borderWidth: 1
        }
      ]
    };

    setChartData(chartData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>16:10 Aspect Ratio Calculator</title>
          <meta name="description" content="Calculate the dimensions for a 16:10 aspect ratio based on the given width or height." />
          <meta name="keywords" content="aspect ratio calculator, 16:10 calculator, image dimensions" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🔍</div>
            <h1 className="text-3xl font-bold text-gray-800">16:10 Aspect Ratio Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the dimensions for a 16:10 aspect ratio based on the given width or height.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Width (optional):</label>
              <input
                type="text"
                value={width}
                onChange={handleWidthChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter width"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Height (optional):</label>
              <input
                type="text"
                value={height}
                onChange={handleHeightChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter height"
              />
            </div>
          </div>

          <button
            onClick={calculateDimensions}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Dimensions
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {(width || height) && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Width</h3>
                  <p className="text-lg">{width || 'N/A'}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Height</h3>
                  <p className="text-lg">{height || 'N/A'}</p>
                </div>
              </div>

              {chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Dimensions'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Aspect Ratio'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: '16:10 Aspect Ratio Visualization'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the 16:10 Aspect Ratio Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Width or Height:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input either the width or height of the image or screen.</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>The calculator will determine the missing dimension based on the 16:10 aspect ratio.</li>
                </ul>
              </li>
              <li><strong>Review Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Check the calculated width and height.</li>
                  <li>Visualize the dimensions using the bar chart.</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use this calculator for designing images, videos, or screens with a 16:10 aspect ratio.</li>
                <li>Ensure the entered value is a valid number.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation</h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a 16:10 aspect ratio:</p>
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Width: 1600</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Enter the width (1600).</li>
                <li>Click "Calculate Dimensions".</li>
                <li>The calculator will determine the height based on the 16:10 aspect ratio.</li>
              </ol>

              <h4 className="mb-4">Results:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Width: 1600</li>
                <li>Height: 1000</li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
          <div className="text-gray-600">
            <p>Here are some frequently asked questions about the 16:10 Aspect Ratio Calculator:</p>
            <div className="mt-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What is the 16:10 aspect ratio?</strong>
                <p>The 16:10 aspect ratio is a common screen resolution format where the width is 16 units and the height is 10 units.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: How do I use the calculator?</strong>
                <p>Enter either the width or height of your image or screen, and the calculator will determine the other dimension based on the 16:10 aspect ratio.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: Why is the aspect ratio important?</strong>
                <p>The aspect ratio is important for maintaining the correct proportions of images and videos, ensuring they are displayed correctly without distortion.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AspectRatioCalculator;